'use strict';

export class MapAnimator {
    constructor(conf) {
        this.playbackMilliseconds = conf.playbackMilliseconds || false; // Vai playback laiki ir milisekundēs (true) vai sekundēs (false)
        const multiplier = this.playbackMilliseconds ? 1000 : 1;
        this.queue = conf.queue; // Masīvs ar notikumiem, sakārtots augoši pēc laika
        this.delay = (conf.delay || 0) * multiplier; // Noklusējuma 2 minūšu delay (120 sekundes)
        this.fetchMargin = (conf.fetchMargin || 0) * multiplier; // Cik sekundes pirms datu beigām pieprasīt nākamo porciju (ja ir nepieciešams)
        this.paused = conf.paused || false; // Vai animators ir pauzēts
        this.lastId = conf.lastId || null; // Pēdējā apstrādētā notikuma ID pēc kā meklējam nākamos notikumus

        this.missedCalcSeconds = conf.missedCalcSeconds || 1; // Vai aprēķināt nokavētos notikumus, sekundes, vai false
        this.missedOffset = conf.missedOffset || -200; // Nokavēto notikumu dzēšanas offset (piem., -200 milisekundes)

        this.missedCalcTime = this.missedCalcSeconds ? (this.missedCalcSeconds * (this.playbackMilliseconds ? 1000 : 1)) : 0;

        this.loop = conf.loop || false; // Vai animators lieto fetch lai turpinātu ielādi pēc queue izsīkšanas
        this.animate = conf.animate || null; // Animācijas izsaukšanas funkcija (piem., window.triggerBeamAnimation)
        this.fetch = conf.fetch || null; // Funkcija datu ielādei (piem., window.fetchMiracles)
        this.finish = conf.finish || null; // Funkcija, ko izsaukt, kad animācijas beidzas
        this.finished = false; // Vai animācijas ir pabeigtas

        // 1. Virtuālais laiks (piem. serveris 16:32:00, bet atskaņojam 16:30:00)
        this.playbackTime = conf.serverNowTimestamp - this.delay;
        this.lastFetchLimit = conf.serverNowTimestamp;     // Līdz cikiem mums ir dati
        this.maxPlaybackTime = conf.maxPlaybackTime || null; // Maksimālais atskaņošanas laiks, pēc kura apstājamies (ja nepieciešams)

        if (conf.startPlayingAt) {
            this.startPlayingAt = conf.startPlayingAt;
            this.playbackTime = this.startPlayingAt;
            this.cleanMissed();
        }

        this.isFetching = false;

        // Tehniskais laiks precīzam ciklam
        this.lastLocalTick = performance.now();

        this.abortController = null;

        this.bindEvents();

        // Sākam ciklu
        this.tick = this.tick.bind(this);

        //console.log('Initial paused state:', this.paused);
        if (!this.paused) {
            if (document.hidden) {
                this.paused = true;
                return;
            }
            //console.log('Starting animator.');
            requestAnimationFrame(this.tick);
        }
    }

    bindEvents() {
        this.boundVisibility = (event) => this.handleVisibilityChange();
        this.boundPageHide = (event) => {
            // event.persisted = true nozīmē, ka pārlūks mēģina lapu ielikt BFCache atmiņā
            if (event.persisted) {
                //console.log("❄️ Lapa ielikta BFCache. Apturam visu!");
                this.stop();
                // Ja tev ir aktīvi AJAX pieprasījumi (fetch), te būtu laba vieta
                // izsaukt AbortController.abort(), bet tas jau ir pedantismam.
                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }
            }
        };
        this.boundPageShow = (event) => {
            // event.persisted = true nozīmē, ka lapa tika izvilkta no BFCache,
            // NEVIS ielādēta no jauna.
            if (event.persisted) {
                //console.log("🔥 Lapa atkausēta no BFCache!");
                this.start();
            }
        };

        //console.log('Constructor called for MapAnimator instance.', this.paused);
        document.addEventListener('visibilitychange', this.boundVisibility);

        // 2. BFCache "Iesaldēšanai" (kad lietotājs aiziet uz citu lapu)
        window.addEventListener('pagehide', this.boundPageHide);

        // 3. BFCache "Atkausēšanai" (kad lietotājs nospiež "Back" pogu)
        window.addEventListener('pageshow', this.boundPageShow);
    }
    destroy() {
        //console.log("🧹 Tīrām MapAnimator un gatavojamies dzēšanai...");

        // A. Apturam visus procesus
        this.stop();

        // B. Atceļam aktīvos AJAX, ja tādi ir
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }

        // C. NOŅEMAM visus globālos eventus (Izmantojot tās pašas atsauces!)
        document.removeEventListener('visibilitychange', this.boundVisibility);
        window.removeEventListener('pagehide', this.boundPageHide);
        window.removeEventListener('pageshow', this.boundPageShow);

        // D. Iztīrām jebkādus atmiņā iestrēgušus masīvus vai elementus
        this.boundVisibility = null;
        this.boundPageHide = null;
        this.boundPageShow = null;
    }

    setAbortController(controller) {
        this.abortController = controller;
    }

    handleVisibilityChange() {
        if (document.hidden) {
            //console.log('Document hidden, stopping animator.', document.hidden);
            this.stop();
        } else {
            //console.log('Document visible, starting animator.', document.hidden);
            this.start();
        }
    }

    stop() {
        this.paused = true;
    }

    start() {
        if (!this.paused) return;

        this.paused = false;

        // Pārliecināmies, ja pauze ir bijusi ilgāk par sekundi, tad atjaunojam playback laiku un nodzēšam nokavētos notikumus
        const now = performance.now();
        const deltaTime = (now - this.lastLocalTick) / (this.playbackMilliseconds ? 1 : 1000); // Ja rēķinām milisekundēs, dalām ar 1, citādi dalām ar 1000, lai iegūtu sekundes
        this.playbackTime += deltaTime;
        if (this.queue.length > 0) {
            if (this.missedCalcTime && deltaTime >= this.missedCalcTime) {
                // nodzēšam nokavētos notikumus
                if (this.missedCalcSeconds) {
                    this.cleanMissed();
                }
            }
        }
        this.lastLocalTick = now; // Atjaunojam pēdējo lokālo laiku, lai neradītu lielu delta nākamajā ciklā

        // Ja queue ir tukša, pie nākošā tick tiks pieprasīti jauni dati, ja nepieciešams

        requestAnimationFrame(this.tick);
    }

    cleanMissed() {
        while (this.queue.length > 0 && this.queue[0].timestamp < this.playbackTime + this.missedOffset) {
            //console.log(`Nodzēsts nokavētais notikums ar timestamp: ${this.queue[0].timestamp}`);
            this.queue.shift();
        }
    }

    tick() {
        const now = performance.now();
        const deltaTime = (now - this.lastLocalTick) / (this.playbackMilliseconds ? 1 : 1000); // Ja rēķinām milisekundēs, dalām ar 1, citādi dalām ar 1000, lai iegūtu sekundes
        this.lastLocalTick = now;

        // Kustinām mūsu virtuālo "servera" atskaņošanas laiku uz priekšu
        this.playbackTime += deltaTime;

        if (this.missedCalcTime && deltaTime >= this.missedCalcTime) {
            // nodzēšam nokavētos notikumus (tabs bijis fonā)
            if (this.missedCalcSeconds) {
                this.cleanMissed();
            }
        }

        // 1. Spēlējam animācijas
        this.playQueuedAnimations();

        // 2. Pārbaudām, vai nav laiks lūgt jaunus datus
        this.checkBuffer();

        // Ja animators ir pauzēts, pārtraucam ciklu šeit
        if (this.paused) return;

        if (this.maxPlaybackTime !== null && this.playbackTime >= this.maxPlaybackTime) {
            //console.log(`Sasniegts maksimālais atskaņošanas laiks: ${this.maxPlaybackTime}`);
            // Sasniegts maksimālais atskaņošanas laiks, apstājam animatoru
            if (this.finish && !this.finished) {
                this.finish();
            }
            this.finished = true;
            this.stop();
            return;
        }

        // Cikls turpinās bezgalīgi (līdz aizver lapu)
        requestAnimationFrame(this.tick);
    }

    playQueuedAnimations() {
        // Kamēr rindā ir notikumi un to laiks ir pienācis (vai jau garām, ja tab's bija fonā)
        while (this.queue.length > 0 && this.queue[0].timestamp <= this.playbackTime) {
            const event = this.queue.shift(); // Izņemam no rindas

            // ŠEIT SAUC TAVU ESOŠO ANIMĀCIJAS SKRIPTU
            //console.log(`[${new Date(this.playbackTime * (this.playbackMilliseconds ? 1 : 1000)).toLocaleTimeString()}] Animācija:`, event);

            if (this.animate) {
                this.animate(event);
            }
        }
    }

    async checkBuffer() {
        if (this.isFetching) return;
        if (!this.loop) return;

        // Ja mūsu atskaņošanas laiks ir pietuvojies pēdējai ielādētajai robežai (palikušas 10 sekundes)
        if (this.playbackTime >= (this.lastFetchLimit - this.fetchMargin)) {
            this.isFetching = true;

            try {
                // Sūtam pieprasījumu, prasot VISU, kas ir jaunāks par lastFetchLimit
                if (this.fetch) {
                    // Padodam arī playbackTime, lai serveris zinātu, cik tālu mēs esam atskaņošanā. Ja bijusi pauze pārāk ilgi, lai serveris var atgriezt tikai jaunākos notikumus nevis visus nokavētos
                    const data = await this.fetch(this, this.lastId, this.lastFetchLimit, this.playbackTime, this.delay);
                    if (!data) {
                        this.stop();
                        this.isFetching = false;
                        return; // Pārtraucam tālāku izpildi, ja nav datu
                    }

                    // Pievienojam jaunos notikumus esošās rindas beigās
                    if (data.events && data.events.length > 0) {
                        this.queue = this.queue.concat(data.events);
                    }

                    if (!data.server_now) {
                        this.stop();
                        this.isFetching = false;
                        return; // Pārtraucam tālāku izpildi, ja nav server_now
                    }

                    // Serverim OBLIGĀTI jāatgriež jaunais `server_now` savā atbildē,
                    // lai mēs zinām, līdz kuram brīdim datubāzē nav datu
                    this.lastFetchLimit = data.server_now;
                    this.lastId = data.last_id;

                    if (data.sync_clock) {
                        //console.log(this.playbackTime, data.sync_clock);
                        // Ja serveris norāda, ka atskaņošana jāsāk no konkrēta laika, pielāgojam playbackTime, ja tas ir agrāks.
                        if (this.playbackTime < data.sync_clock) {
                            //console.log("Adjusting playbackTime to sync_clock:", data.sync_clock);
                            this.playbackTime = data.sync_clock;
                        }
                    }
                }
            } catch (error) {
                console.error("Could not fetch new events:", error);
                // Ja nobrūk, atkārtosies nākamajā tick()
                // pagaidām stop savādāk baigais flood
                this.stop();
            } finally {
                this.isFetching = false;
            }
        }
    }
}
